"use client";
import { useState, useEffect } from "react";
import CK_Schemas from "../../../../DataServices/CK_SchemasDS";
import CkDictionaryDs from "../../../../DataServices/CkDictionaryDs";
import { useCustomContext } from "../../../CustomComponents/CustomComponents";
import { insertLog } from "../../../Logs/InsertLog";
export default function AddFieldLogic({ onSave, editData }) {
  const { showAlert, stopHudRotation, hud } = useCustomContext();

  const ds = new CK_Schemas();
  const res = new CkDictionaryDs();

  const [errors, setErrors] = useState({});
  const [dictionaryList, setDictionaryList] = useState([]);
  const useremail = localStorage.getItem("email");
  const [formData, setFormData] = useState({
    field_name: "",
    data_type: "",
    ui_type: "",
    label: "",
    visibility: "",
    source: "",
    help_text: "",
    ck_dictionary_id: "",
    value_separator: "",
    options: [{ text: "", value: "" }],
    rules: [],
    fields: []
  });
  const allowManualOptions = formData.ui_type === "dropdown";

  // Fetches available dictionaries for dropdown field configuration
  useEffect(() => {
    loadDictionaryList();
  }, []);

  const loadDictionaryList = async () => {
    try {
      const req = {};
      const response = await res.CK_Dictionary_List_GET(req);
      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 && Array.isArray(body.data)) {
        setDictionaryList(body.data);
      } else {
        insertLog(
          `${useremail?.split('@')[0]} failed to load Dictionaries`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'CK_Dictionary_List_GET',
          'FAILED',
          'Unable to load Dictionaries'
        );
        showAlert(body?.message || "Failed to load Dictionaries.", [
          { label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }
        ]);
      }
    } catch (err) {
      insertLog(
        `${useremail?.split('@')[0]} failed to fetch Dictionaries`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'CK_Dictionary_List_GET',
        'FAILED',
        err.message
      );
      showAlert(
        err.message || "Something went wrong while fetching Dictionaries.",
        [{ label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }]
      );
    }
  };

  // Prefill edit data
  useEffect(() => {
    if (editData) {
      setFormData({
        field_name: editData.field_name || "",
        data_type: editData.data_type || "",
        ui_type: editData.ui_type || "",
        label: editData.label || "",
        visibility: editData.visibility || "",
        source: editData.source || "",
        help_text: editData.help_text || "",
        ck_dictionary_id: editData.ck_dictionary_id || "",
        value_separator: editData.value_separator || "",
        options: editData.options?.length > 0 ? editData.options : [{ text: "", value: "" }],
        rules: editData.rules || [],
        fields: editData.fields || []
      });

      // Load options if editing dropdown with dictionary_id 
      if (
        editData.ck_dictionary_id &&
        (!editData.options || editData.options.length === 0)
      ) {
        loadDictionaryItems(editData.ck_dictionary_id);
      }

    }
  }, [editData]);

  //  Load dictionary items dynamically based on selected dictionary ID
  const loadDictionaryItems = async (dictionaryId) => {
    if (!dictionaryId) return;

    hud();
    try {
      const result = await ds.WS_CK_Dictionary_Items_ByID_GET({
        ck_dictionary_id: dictionaryId,
      });

      const body = result?.data?.body ? JSON.parse(result.data.body) : result.data || {};
      const items = body?.data || [];

      setFormData((prev) => ({
        ...prev,
        options: items.length > 0
          ? items.map((i) => ({
            text: i.text || "",
            value: i.value || "",
          }))
          : prev.options,
      }));

    } catch (err) {
      setFormData((prev) => ({
        ...prev,
        options: [{ text: "", value: "" }],
      }));
    } finally {
      stopHudRotation();
    }
  };

  // Handles value separator input and validates allowed characters
  const handleValueSeparatorChange = (e) => {
    const allowed = ['-', ':', '_', ','];
    const val = e.target.value;

    // allow only empty or allowed single character
    if (val === "" || allowed.includes(val)) {
      setFormData((prev) => ({
        ...prev,
        value_separator: val
      }));

      setErrors((prev) => ({
        ...prev,
        value_separator: val.trim() ? undefined : "Value separator is required"
      }));
    }
  };

  //Updates visibility and auto-sets source field
  const handleVisibilityChange = (e) => {
    const { value } = e.target;
    let autoSource = "";
  
    if (value === "hidden") {
      autoSource = "system"; 
    } else if (value === "readonly") {
      autoSource = "user";  
    } else if (value === "editable") {
      autoSource = "user";   
    }
  
    setFormData((prev) => ({
      ...prev,
      visibility: value,
      source: autoSource
    }));
  
    setErrors((prev) => ({
      ...prev,
      visibility: value ? undefined : prev.visibility,
      source: autoSource ? undefined : prev.source
    }));
  };
  

  //Handles form input changes and resets dependent fields automatically
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      if (name === "data_type") {
        // ALWAYS reset ui_type when datatype changes
        updated.ui_type = "";
        updated.value_separator = "";
        updated.options = [{ text: "", value: "" }];
        updated.ck_dictionary_id = "";
      }

      if (name === "ui_type") {
        // clear dictionary & options if not dropdown
        if (value !== "dropdown") {
          updated.options = [{ text: "", value: "" }];
          updated.ck_dictionary_id = "";
        }

        // clear value separator if not multiparttextfield
        if (value !== "multiparttextfield") {
          updated.value_separator = "";
        }
      }

      if (name === "ck_dictionary_id" && !editData) {
        updated.options = [{ text: "", value: "" }];
      }

      return updated;
    });

    setErrors((prev) => ({
      ...prev,
      [name]: value.trim() ? undefined : prev[name],
      ...(name === "data_type"
        ? { ui_type: undefined, value_separator: undefined }
        : {}),
      ...(name === "ui_type" && value !== "multiparttextfield"
        ? { value_separator: undefined }
        : {})
    }));

    if (name === "ck_dictionary_id" && value && !editData) {
      loadDictionaryItems(value);
    }

  };
  // Add new empty option row
  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { text: "", value: "" }],
    }));
  };
  // Update specific option field(text or value)
  const updateOption = (index, key, value) => {
    const updated = [...formData.options];
    updated[index] = { ...updated[index], [key]: value };
    setFormData((prev) => ({ ...prev, options: updated }));
  };
  // Remove option row (keep at least 1 empty option)
  const removeOption = (index) => {
    if (formData.options.length > 1) {
      const updated = formData.options.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, options: updated }));
    }
  };

  // Validates all required fields based on field type
  const validateFormData = (data) => {
    const newErrors = {};

    if (!data.field_name.trim()) newErrors.field_name = "Field Name is required";
    if (!data.data_type.trim()) newErrors.data_type = "Data Type is required";
    if (!data.ui_type.trim()) newErrors.ui_type = "UI Type is required";
    if (!data.label.trim()) newErrors.label = "Label is required";
    if (!data.visibility.trim()) newErrors.visibility = "Visibility is required";
    if (!data.source.trim()) newErrors.source = "Source is required";
    if (
      data.ui_type === "multiparttextfield" &&
      !data.value_separator.trim()
    ) {
      newErrors.value_separator = "Value separator is required";
    }
    if (
      (data.data_type === "group" || data.data_type === "list") &&
      !data.help_text?.trim()
    ) {
      newErrors.help_text = "Help text is required";
    }
    return newErrors;
  };

  // Trims and collapses multiple spaces into single space
  const normalizeValue = (value = "") =>
    value.trim().replace(/\s+/g, " ");

  // Validates and saves field data
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateFormData(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const cleanedData = {
      ...formData,

      field_name: normalizeValue(formData.field_name),
      label: normalizeValue(formData.label),
      help_text: normalizeValue(formData.help_text),
      source: normalizeValue(formData.source),

      options: formData.options
        .map((opt) => ({
          text: normalizeValue(opt.text),
          value: normalizeValue(opt.value),
        }))
        .filter(
          (opt) => opt.text !== "" || opt.value !== ""
        ),
    };

    onSave(cleanedData);

  };

  return {
    formData,
    dictionaryList,
    handleChange,
    handleSubmit,
    addOption,
    updateOption,
    removeOption,
    setFormData,
    errors,
    allowManualOptions,
    handleValueSeparatorChange,
    handleVisibilityChange
  };
}
